import type { Sphere, SatelliteSphere } from 'types/global';
import type { SphereNode } from 'types/context';

export interface GravityRange {
  min: number;
  max: number;
}

export interface OrbitConfig {
  radius: number;
  colors: string[];
  speed: number; // Base speed in ms for full rotation (modified by velocity)
  nodeCount?: number;
  gravityRange?: GravityRange; // Gravity score range for this orbit
}

export interface NodeData {
  id: string;
  label: string;
  icon?: string;
  image?: string;
  imagePath?: string;  // Path to avatar/icon image
  customLabel?: string;  // Text label to display next to node
  labelBackground?: boolean;  // Show gradient background for label
  satelliteCount?: number;  // Number of small orbiting circles (0-5) - deprecated, use satellites
  satellites?: SatelliteSphere[];  // Actual satellite sphere data from API (max 5 displayed)
  onClick?: () => void;

  // Gravity/velocity fields for armillary prioritization
  velocity?: number;      // -100 to +100 (for animation speed)
  gravity?: number;       // 0-100 (for orbit assignment)
  urgency?: number;       // 1-5 (contextual urgency from parent)
  impact?: number;        // 0-100 (scope of influence)
  sphereData?: Sphere;    // Legacy: full Sphere data (for locally-added spheres)
  sphereNode?: SphereNode; // Context API node data
}

// Orbital configurations for Armillary view
// Outer rings fade with lower opacity (disappearing into space)
// All rings use blue tones that fade from visible to subtle
// gravityRange determines which spheres go on which orbit (higher gravity = inner orbit)
// Enhanced with gradient shimmer colors for animated loader effect
export const ORBITS: OrbitConfig[] = [
  { radius: 48, colors: ['#1a3050'], speed: 20000, gravityRange: { min: 80, max: 100 } },  // Critical priority (~20s rotation)
  { radius: 138, colors: ['#356EA4', '#2a5a8a', '#4d7fb8'], speed: 30000 },                 // Background ring (status color applied dynamically)
  { radius: 156, colors: ['#2a5585', '#1e4a75', '#356ba4', '#1a3d65'], speed: 35000 },      // Inner visible ring - brighter blue with shimmer
  { radius: 193, colors: ['#1e4570', '#193a60', '#2a5585', '#152f50'], speed: 45000, gravityRange: { min: 60, max: 79 } },  // Mid ring with gradient
  { radius: 230, colors: ['#183555', '#142a48', '#1e4570', '#10223a'], speed: 60000, gravityRange: { min: 40, max: 59 } },  // Fading ring with shimmer
  { radius: 266, colors: ['#122840', '#0e2035', '#183555', '#0a182a'], speed: 75000, gravityRange: { min: 20, max: 39 } },  // Darker blue ring
  { radius: 303, colors: ['#0c1e30', '#081828', '#122840', '#061220'], speed: 90000, gravityRange: { min: 0, max: 19 } },   // Fades into space (subtle blue)
];

// Light theme orbital ring colours — silver-grey palette matching Figma design
// Inner rings are more saturated grey-blue; outer rings fade to near-white
export const ORBITS_LIGHT: OrbitConfig[] = [
  { radius: 48,  colors: ['#7a8ca8'], speed: 20000, gravityRange: { min: 80, max: 100 } },
  { radius: 138, colors: ['#8090a8', '#7080a0', '#90a0b8'], speed: 30000 },
  { radius: 156, colors: ['#90a0b8', '#8090a8', '#a0b0c8', '#7080a0'], speed: 35000 },
  { radius: 193, colors: ['#a8b8cc', '#98a8bc', '#b8c8d8', '#90a0b8'], speed: 45000, gravityRange: { min: 60, max: 79 } },
  { radius: 230, colors: ['#b8c8d8', '#a8b8cc', '#c4d0e0', '#a0b0c8'], speed: 60000, gravityRange: { min: 40, max: 59 } },
  { radius: 266, colors: ['#c8d4e0', '#bcc8d8', '#d4dce8', '#b0bece'], speed: 75000, gravityRange: { min: 20, max: 39 } },
  { radius: 303, colors: ['#d4dce8', '#ccd4e4', '#dce4ee', '#c4ccd8'], speed: 90000, gravityRange: { min: 0, max: 19 } },
];

// Center SVG viewBox dimensions
export const SVG_WIDTH = 800;
export const SVG_HEIGHT = 800;
export const CENTER_X = SVG_WIDTH / 2;
export const CENTER_Y = SVG_HEIGHT / 2;

// Node styling (increased to match original)
export const NODE_RADIUS = 30;
export const CENTER_TEXT_SIZE = 40;

// Label collision detection - dial radius for label flip threshold
export const DIAL_RADIUS = 115;
export const LABEL_OFFSET = 35; // How far label starts from node center

// Settlement animation timing
export const SPINNING_PHASE_DURATION = 10000; // ms before orbits begin settling
export const DECELERATION_DURATION = 2500; // ms for easeQuadOut deceleration to rest
export const SETTLEMENT_STAGGER_MS = 200; // ms delay between each orbit's settlement start
export const MIN_SETTLEMENT_GAP_RAD = 0.05; // minimum angular gap (radians) between settled nodes
export const ORBIT_STAGGER_ANGLE = Math.PI * (3 - Math.sqrt(5)); // golden angle (~2.399 rad / ~137.5°) — maximizes angular separation between adjacent orbits

// Animation lifecycle phases
export type AnimationPhase = 'spinning' | 'decelerating' | 'settled';